import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useHistory, useParams } from 'react-router';

import { Box, Button, Container, Stack, Typography } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import Spinner from '~/components/Common/Spinner';

import useCurrentTenant from '~/hooks/useCurrentTenant';

import { formatUTCDateDateTimeWidget } from '~/services/TimeService';
import { getTourDealById, updateTourDeal } from '~/services/ToursService';

import TourDealPageForm from './components/TourDealPageForm';
import { TourDealPage, TourDealPageBody } from './types';

function DealPageEdit() {
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();

  const [dealPage, setDealPage] = useState<TourDealPage | null>(null);
  const [fetching, setFetching] = useState(false);

  const { tenant } = useCurrentTenant();

  const handleSubmit = useCallback(
    async (formValues: TourDealPageBody) => {
      setFetching(true);

      try {
        await updateTourDeal(id, {
          ...formValues,
          id,
          startAt: formatUTCDateDateTimeWidget(new Date(formValues.startAt)),
          endAt: formatUTCDateDateTimeWidget(new Date(formValues.endAt)),
        });
        enqueueSnackbar('Deal page updated successfully', { variant: 'success' });
        history.push('/tours/deals');
      } catch (error) {
        enqueueSnackbar(`Error updating deal page: ${error}`, { variant: 'error' });
      } finally {
        setFetching(false);
      }
    },
    [enqueueSnackbar, history, id],
  );

  const fetchDealPage = useCallback(async () => {
    const result = await getTourDealById(id);
    setDealPage(result);
  }, [id]);

  useEffect(() => {
    fetchDealPage();
  }, [fetchDealPage]);

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Edit Tour Deal Page</title>
      </Helmet>

      <PageHeader title="Edit Tour Deal Page" backButton="/tours/deals">
        {dealPage && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography>Preview on:</Typography>
            <Button
              variant="text"
              target="_blank"
              rel="noreferrer"
              href={`${window.configs.LUX_CUSTOMER_PORTAL}/tours/deal/${dealPage.slug || dealPage.id}`}
            >
              {tenant.title}
            </Button>
          </Stack>
        )}
      </PageHeader>

      {fetching && <Spinner size={30} />}

      {dealPage && !fetching && (
        <Box>
          <Stack mt={1} spacing={2}>
            <TourDealPageForm dealPage={dealPage} onSubmit={handleSubmit} />
          </Stack>
        </Box>
      )}
    </Container>
  );
}

export default DealPageEdit;
